import { api } from '@/lib/api.js';
import { toast } from '@/components/ui/toaster.jsx';
import { inFlightDedup } from '@/lib/inFlight.js';

// Structured quotes (from the `quotes` table) and inbound quote
// documents (from `inbound_documents`) for a deal.

export function getQuotesForDeal(dealId) {
  return inFlightDedup(`quotes:${dealId}`, () =>
    api.get(`/api/tracon/quotes/${encodeURIComponent(dealId)}`)
  );
}

// GET /api/tracon/quotes/all?account_id=… — every Quotation that lives
// on a deal belonging to the given account. Server uses the cached
// pipeline to resolve account → deal-ids without re-hitting Zoho per
// request. Returns the full quote shape including aircraft / coverage
// flags / status — enough to render QuotesPanel grouped by deal
// (Vision §V2 fix #9 2026-05-20).
export function getQuotesForAccount(accountId) {
  return inFlightDedup(`quotes:account:${accountId}`, () =>
    api.get(`/api/tracon/quotes/all?account_id=${encodeURIComponent(accountId)}`)
  );
}

// GET /api/tracon/deals/:dealId/quotes — the same panel rows as
// getQuotesForAccount, scoped to one deal and served WITHOUT the cached Zoho
// pipeline. The markets ladder uses this: it already knows its deal, and the
// account-scoped route blocks on a pipeline rebuild that every quick-log has
// just invalidated (prompt 67 item 2.2).
//
// NOT dedup-wrapped. inFlightDedup collapses concurrent identical calls, and the
// call right after a quick-log must not be answered by a request that was
// already in flight before the quote existed.
export function getQuotesForDealPanel(dealId) {
  return api.get(`/api/tracon/deals/${encodeURIComponent(dealId)}/quotes`);
}

export function getQuoteDocs(dealId) {
  return inFlightDedup(`quote-docs:${dealId}`, () =>
    api.get(`/api/tracon/quote-docs/${encodeURIComponent(dealId)}`)
  );
}

// POST /api/tracon/quotes/create — Quick Log + Full Quote both go through
// this same endpoint. Full Quote sends the complete body shape (incl.
// aircraft_schedule[], coverage_parts[], opw_*, etc.); Quick Log sends a
// subset. The server's NPO guard (`applyNpoGuard`) zeroes OPW_* fields
// server-side when named_pilots_only: true, so the UI just needs to
// not surface the OPW inputs in NPO mode.
export function logQuoteReceived(payload) {
  return api.post('/api/tracon/quotes/create', payload);
}

// Hangar/property coverage-line quote (COVERAGE-LINE-ENVELOPE-SPEC §7 Slice 5).
// Posts the canonical { deal_id, carrier, effective_date, client_visible,
// present, subject_to, lines[] } envelope; with present:true the backend also
// flips the deal to Presented so a client-visible quote surfaces in Tower.
// One envelope quote with its coverage lines (broker edit-sheet prefill).
export function getQuoteFull(quoteId) {
  return api.get(`/api/tracon/quotes/${encodeURIComponent(quoteId)}/full`);
}

export function logHangarQuote(payload) {
  return api.post('/api/tracon/quotes/log-hangar', payload);
}

// The default for the hangar sheet's "SNKL at binding" box: { checked, source, reason }
// (this client's last placement, then the carrier's setting). A suggestion, never a gate.
export function getSnklSuggestion(dealId) {
  return api.get(`/api/snkl/deal/${encodeURIComponent(dealId)}/suggestion`);
}

// GL/commercial coverage-line quote (§7 Slice 4a). Same canonical create
// endpoint as hangar (policy-type-agnostic); the payload carries
// policy_type:'commercial_gl'. present:true flips the deal to Presented.
export function logGlQuote(payload) {
  return api.post('/api/tracon/quotes/log-hangar', { ...payload, policy_type: 'commercial_gl' });
}

// Broker list of a deal's envelope quotes (hangar / commercial_gl), visible or
// hidden, for the Edit door on the commercial Coverage panel.
export function getEnvelopeQuotes(dealId) {
  return api.get(`/api/tracon/deals/${encodeURIComponent(dealId)}/envelope-quotes`);
}

// PROPERTY-LINE P2 — builders risk market response as a canonical envelope
// quote (one builders_risk line + optional tria split). Never presents and
// never touches the deal's session/Tower state; presentation is P4.
export function logPropertyQuote(payload) {
  return api.post('/api/tracon/quotes/log-property', payload);
}

// The property deal's entered market responses, newest first (broker shape:
// premium {base, tria, total} + the builders_risk terms bag).
export function getPropertyQuotes(dealId) {
  return inFlightDedup(`property-quotes:${dealId}`, () =>
    api.get(`/api/tracon/deals/${encodeURIComponent(dealId)}/property-quotes`)
  );
}

// §7 Slice 5 (gap A) — attach the carrier's source quote PDF to a logged
// coverage-line quote. Reuses the existing /drop-pdf quote-attach pipeline:
// uploads to WorkDrive + files an inbound_documents row with matched_quote_id
// (document_type 'quote', no AI classifier). Surfaced via getQuoteDocs.
// Multipart, so raw fetch + JWT (the shared api wrapper hardcodes JSON);
// the browser sets the multipart boundary — no Content-Type header.
export async function attachQuoteSourcePdf(dealId, quoteId, file) {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('deal_id', dealId);
  fd.append('quote_id', quoteId);
  const res = await fetch(`/api/tracon/drop-pdf?_t=${Date.now()}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${localStorage.getItem('tracon_token')}` },
    body: fd,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `source-PDF attach failed (HTTP ${res.status})`);
  }
  return res.json();
}

// PATCH /api/tracon/quotes/:id — used by Full Quote modal in edit mode.
// IMPORTANT: aircraft_schedule[] rows MUST round-trip their `id` field
// when editing an existing quote — Zoho APPENDS new rows when id is
// missing, doubling the subform on every save (CLAUDE.md recurring
// production bug). The frontend hands the id back transparently.
// Fork a quote into a live SIBLING (wave 3 item 5). An alternate, not a
// revision: the original stays exactly as it was and neither supersedes the
// other. Returns the new quote id so the caller can open the wizard on it.
export function forkQuote(quoteId) {
  return api.post(`/api/tracon/quotes/${encodeURIComponent(quoteId)}/fork`, {});
}

// Every quote editor saves through here, so the "you just edited the client's
// pick" notice lives here once rather than in four sheets that could forget it.
// The server only sets client_pick when the edited quote IS the pick and the
// pick no longer matches it.
export function patchQuote(quotationId, payload) {
  return api.patch(
    `/api/tracon/quotes/${encodeURIComponent(quotationId)}`,
    payload
  ).then((r) => {
    const cp = r?.client_pick;
    if (cp?.stale) {
      const was = cp.premium != null ? ` still shows $${Number(cp.premium).toLocaleString('en-US')}` : ' is out of date';
      toast.info(cp.can_send_back
        ? `The client selected this quote before your edit. Their selection on the deal${was}. Use "Send back to client" or "Offer both" on the deal.`
        : `The client selected this quote before your edit. Their selection on the deal${was}. ${cp.reason || ''}`.trim());
    }
    // The save's own warnings (a visible quote that could not be re-presented
    // after who it names changed, final review OW-1): one toast each.
    (Array.isArray(r?.warnings) ? r.warnings : []).forEach((w) => toast.info(w));
    return r;
  });
}

// GET /api/tracon/quotes/:id — single quote with full subforms (for the
// edit-mode pre-population of the Full Quote modal).
// Add a SECOND option to a quote: the six-month premium beside the annual.
// `term_variant_of` makes it a term VARIANT, so the backend copies the parent's
// coverage rather than trusting a retyped copy of it.
export function addQuoteOption(quoteId, payload) {
  return api.post(`/api/tracon/quotes/${encodeURIComponent(quoteId)}/options`, payload);
}

// Withdraw a term variant. The backend refuses anything that is not one, so
// this cannot remove the option the quote is built on.
export function deleteQuoteOption(quoteId, optionId) {
  return api.del(`/api/tracon/quotes/${encodeURIComponent(quoteId)}/options/${encodeURIComponent(optionId)}`);
}

// `optionId` opens a NAMED option instead of the quote's primary (the picker).
// BY ID ONLY: option_number repeats on the same quote (f8edf033 carries 1, 2, 2,
// 1), so an ordinal addresses two rows with one click. The dedup key carries it
// too, or switching options would be served the previous option's response.
export function getQuote(quotationId, optionId) {
  const qs = optionId ? `?option_id=${encodeURIComponent(optionId)}` : '';
  return inFlightDedup(`quote:${quotationId}:${optionId || 'primary'}`, () =>
    api.get(`/api/tracon/quotes/${encodeURIComponent(quotationId)}${qs}`)
  );
}

// DELETE /api/tracon/quotes/:id — deletes the CRM Quotation outright.
// Used by QuotesPanel's row-level delete (confirmed via AlertDialog).
export function deleteQuote(quotationId) {
  return api.del(`/api/tracon/quotes/${encodeURIComponent(quotationId)}`);
}

// POST /api/tracon/quotes/:id/present — Save+Present flow. Sets
// Client_Visible=true and Status=Presented; the Comms Hub's
// 'quotes-ready' template draws from client-visible quotes on the deal.
export function presentQuote(quotationId) {
  return api.post(
    `/api/tracon/quotes/${encodeURIComponent(quotationId)}/present`,
    {}
  );
}

// POST /api/tracon/quotes/:id/mirror — re-sync action for the QuotesPanel
// "CRM sync failed" badge. Re-runs the mirror (domain → Zoho Quotation) and
// clears mirror_status back to synced on success.
export function resyncQuote(quotationId) {
  return api.post(`/api/tracon/quotes/${encodeURIComponent(quotationId)}/mirror`, {});
}

// POST /api/tracon/quotes/:id/withdraw — pull a presented quote off the Tower
// (Client_Visible=false). Awaited mirror server-side so the client stops seeing
// it within seconds. Reversible — re-presenting the quote brings it back.
export function withdrawQuote(quotationId) {
  return api.post(`/api/tracon/quotes/${encodeURIComponent(quotationId)}/withdraw`, {});
}

// POST /api/session/admin/record-bind/:dealId — the broker door into the
// selection+bind path. Records a bind that was determined offline (closing
// table, phone): same server code as the client's select-quote, but the bind
// txn carries created_by 'broker-bind' and the quote's past effective date is
// HONORED (coverage is already bound with the carrier), never clamped to
// today. The quote becomes client-visible and the Tower link drops the client
// straight into the signing wizard with the date locked.
export function recordOfflineBind(dealId, quoteId) {
  return api.post(
    `/api/session/admin/record-bind/${encodeURIComponent(dealId)}`,
    { quotation_id: quoteId }
  );
}

// GET /api/tracon/deals/:dealId/quote-prefill — returns the shape the
// Full Quote modal needs to render the per-aircraft Section D table
// (already populated from the deal's Aircraft_on_Policy), plus the
// PART_TYPE_PICKLIST for Section F (Coverage Parts).
// WHO IS ON THIS OFFER. Distinct from the roster door, which decides who is on
// the DEAL: this never touches the deal, the PHF or CRM.
export function setPilotMembership(quoteId, { contact_id, name, on_quote }) {
  return api.post(`/api/tracon/quotes/${encodeURIComponent(quoteId)}/pilot-membership`,
    { contact_id, name, on_quote });
}

// "Run it again without him." Forks the quote with the pilot excluded and the
// premium CLEARED, because a risk-changing fork is born unpriced: the carrier
// gives a new number for a different risk.
export function forkQuoteWithoutPilot(quoteId, { contact_id, name }) {
  return api.post(`/api/tracon/quotes/${encodeURIComponent(quoteId)}/fork`,
    { without_contact_id: contact_id, without_name: name });
}

// `quoteId` is optional and, when given, makes the response carry `quote_roster`:
// who is on THAT offer, split by the server. It is part of the dedup key because
// two quotes on one deal have different rosters and must not share a cached
// response.
export function getQuotePrefill(dealId, quoteId) {
  const qs = quoteId ? `?quote_id=${encodeURIComponent(quoteId)}` : '';
  return inFlightDedup(`quote-prefill:${dealId}:${quoteId || ''}`, () =>
    api.get(`/api/tracon/deals/${encodeURIComponent(dealId)}/quote-prefill${qs}`)
  );
}

// GET /api/tracon/deals/:dealId/presentable-quotes — §7 Slice 5 (gap B). The
// deal's client-visible envelope quotes (hangar/GL) for the "select & send
// checkout" picker in ComposeSheet. Minimal shape: id/carrier/policy_type/premium.
export function getPresentableQuotes(dealId) {
  return inFlightDedup(`presentable-quotes:${dealId}`, () =>
    api.get(`/api/tracon/deals/${encodeURIComponent(dealId)}/presentable-quotes`)
  );
}

// GET /api/tracon/carriers — broker-friendly carrier list (Underwriter
// label + Vendor_Name) for the carrier autocomplete. Cached in-flight.
export function listCarriers() {
  return inFlightDedup('carriers', () => api.get('/api/tracon/carriers'));
}

// POST /api/tracon/command-center/quotes/:quoteId/decline — takes a quote row
// id (not a deal/carrier combo); the broker picks from the deal's existing
// quotes when logging the decline.
//
// WHAT THE CARRIER SAID GOES TO THE MARKET (CC PROMPT 23 Part A). The quote
// keeps its own status='declined'; the reason lands on
// submission_markets.declination_reason through the one decline door, which
// finds or creates the row because a carrier cannot decline something it was
// never sent.
//
// `reasons` is the canonical plural array (web-v2/src/lib/declineReasons.js).
// `detail` is free text, required only when a reason is 'other'. The response
// carries market_recorded so the caller can tell a decline that joined the
// shopping record from one whose carrier would not resolve to a market.
export function logQuoteDecline(quoteId, { reasons, detail } = {}) {
  return api.post(
    `/api/tracon/command-center/quotes/${encodeURIComponent(quoteId)}/decline`,
    { reasons, reason: detail || null }
  );
}

// ─── Submission record (SUBMISSION-RECORD-SPEC) ────────────────────────────
//
// Broker-only. The submitted payload carries pilot PII, so these endpoints sit
// behind the broker JWT and are never reachable from the partner portal.

// GET /api/quotes/:quoteId/submission-record
export function getSubmissionRecord(quoteId) {
  return api.get(`/api/quotes/${encodeURIComponent(quoteId)}/submission-record`, { timeoutMs: 60_000 });
}

// Fetch a carrier PDF as a Blob. It cannot be a plain window.open: the stream
// is JWT-gated and a new tab would send no Authorization header, so the browser
// would land on a 401 instead of the document.
export async function fetchQuoteDoc(quoteId, handlerId) {
  const res = await fetch(
    `/api/quotes/${encodeURIComponent(quoteId)}/docs/${encodeURIComponent(handlerId)}`,
    { headers: { Authorization: `Bearer ${localStorage.getItem('tracon_token')}` } }
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `document fetch failed (HTTP ${res.status})`);
  }
  return res.blob();
}

// The two clearing controls (187 §5). `basis` is 'firmed' (the carrier agreed
// the rate) or 'misclick' (it was never referred) — different claims, kept
// apart in the record. Attributed server-side from the broker JWT.
export function clearQuoteReferral(quoteId, basis) {
  return api.post(`/api/tracon/quotes/${encodeURIComponent(quoteId)}/referral/clear`, { basis });
}
